/**
 * @file driver-reconciliation-report.tsx
 * @module app/modules/finance-reports/driver-reconciliation-report
 * @description Produces driver reconciliation reports with filtering, pagination, and Excel export.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import {
  Button,
  Card,
  Col,
  FormLabel,
  Row,
  Modal,
  Spinner,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  AllModules,
  OrdersDelivery,
  PAGE_LIMIT,
  RouteProductReports,
  View,
} from "../../../utils/constants";
import APICallService from "../../../api/apiCallService";
import { driver, routePlanning, seller } from "../../../api/apiEndPoints";
import Method from "../../../utils/methods";
import Loader from "../../../Global/loader";
import CustomDatePicker from "../../custom/DateRange/DatePicker";
import { useAuth } from "../auth";
import PermissionModal from "../../modals/permission";
import { useWarehouse } from "../../context/WarehouseContext";
import * as XLSX from "xlsx";
import { CustomSelect } from "../../custom/Select/CustomSelect";

const ExcelExportModal = ({
  show,
  onHide,
  startDate,
  endDate,
  driverId,
}: {
  show: boolean;
  onHide: () => void;
  startDate: any;
  endDate: any;
  driverId: any;
}) => {
  const { selectedWarehouse } = useWarehouse();
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [processedRecords, setProcessedRecords] = useState(0);
  const [error, setError] = useState("");

  const handleExport = async () => {
    setIsExporting(true);
    setError("");
    setProgress(0);
    setCurrentStep("Initializing export...");

    try {
      setCurrentStep("Fetching route plans...");

      let params: any = {
        fromDate: startDate
          ? Method.convertDateToFormat(startDate, "YYYY-MM-DD")
          : "",
        toDate: endDate
          ? Method.convertDateToFormat(endDate, "YYYY-MM-DD")
          : "",
        needCount: true,
      };
      if (driverId) {
        params.driver = driverId;
      }
      if (selectedWarehouse?.value) {
        params.warehouse = selectedWarehouse.value;
      }

      let apiService = new APICallService(
        routePlanning.listOutForDeliveryPlans,
        params,
        "",
        "",
        "",
        "",
        RouteProductReports,
        selectedWarehouse?.value || null
      );
      let response = await apiService.callAPI();

      if (!response || !response.records) {
        setError("Failed to fetch route plans");
        return;
      }

      const routePlans = response.records;
      const total = routePlans.length;
      setTotalRecords(total);

      if (total === 0) {
        setError("No route plans found to export");
        return;
      }

      // Prepare Excel workbook
      const workbook = XLSX.utils.book_new();

      // Process each route plan
      let processedCount = 0;

      for (const plan of routePlans) {
        setCurrentStep(
          `Processing route plan ${processedCount + 1} of ${total}...`
        );

        const worksheetData = [];

        // Add route plan summary headers
        // Add route plan summary headers
        const planHeaders = [
          "Plan Date",
          "Driver",
          "Vehicle",
          "Total Orders",
          "Total Amount",
          "Remaining Amount",
        ];
        worksheetData.push(planHeaders);

        // Add route plan summary row
        const planSummaryRow = [
          Method.convertDateToDDMMYYYY(plan.planDate),
          plan.driver?.name,
          `${plan.vehicle.name} - ${plan.vehicle.number}`,
          `${plan.totalOrders} `,
          `${plan.latestTotalCashCollection > 0 ? Method.getGeneralizedAmount(plan.latestTotalCashCollection) : Method.getGeneralizedAmount(plan.totalCashCollection)}`,
          `${plan.latestPendingCashCollection > 0 ? Method.getGeneralizedAmount(plan.latestPendingCashCollection) : 0}`,
        ];
        worksheetData.push(planSummaryRow);

        // Add empty rows for separation
        worksheetData.push(["", "", ""]);
        worksheetData.push(["", "", ""]);

        // Fetch detailed order info for this route
        try {
          setCurrentStep(
            `Fetching orders for route ${plan.driver?.name || "route"}...`
          );

          let params: any = { forLoading: false };
          if (selectedWarehouse?.value) {
            params.warehouse = selectedWarehouse.value;
          }

          let apiService = new APICallService(
            routePlanning.vehicleInfo,
            params,
            { routeId: plan.route, vehicleId: plan.vehicle.reference },
            "",
            "",
            "",
            AllModules,
            selectedWarehouse?.value || null
          );
          let response = await apiService.callAPI();

          if (response && response.orders && response.orders.length > 0) {
            // Add Orders headers
            const orderHeaders = [
              "Order Date",
              "Order ID",
              "Customer Name",
              "Product Name",
              "Quantity",
              "Price",
              "Total Amount",
              "Remaining Amount",
            ];
            worksheetData.push(orderHeaders);
            worksheetData.push(["", "", "", "", "", "", "", ""]); // Empty row for separation

            // Iterate through orders
            response.orders.forEach((order: any) => {
              // Order Summary Row (bold/highlighted in Excel usually)
              const orderDate = Method.convertDateToDDMMYYYY(
                order._createdAt || order.scheduleDate
              );
              const orderRef = order.refKey || "N/A";
              const customerName =
                order.customer?.name || order.placedBy?.user?.name || "N/A";
              const orderTotal = Method.getGeneralizedAmount(
                order.routeVehicle?.paymentCollection?.payment?.totalCharge || 0
              );

              // Add Order Row (spanning or just first columns)
              const remainingAmount = !order.payment?.completed ? order.payment?.remainingCharge || 0 : 0;
              worksheetData.push([
                orderDate,
                orderRef,
                customerName,
                "", // Item Name - empty for order row
                "", // Quantity - empty
                "", // Price - empty
                `${orderTotal}`, // Total Amount for order
                `${Method.getGeneralizedAmount(remainingAmount)}`, // Remaining Amount
              ]);

              // Product Details Rows
              if (
                order.routeVehicle?.variants &&
                order.routeVehicle.variants.length > 0
              ) {
                order.routeVehicle.variants.forEach((item: any) => {
                  // Find matching price info
                  const priceInfo = order.variants?.find(
                    (v: any) =>
                      (v.variant?._id || v.variant) ===
                      (item.variant?._id || item.variant)
                  );

                  const itemName = item.variant?.title || "N/A";
                  const quantity = item.stockCount || 0;
                  const price = Method.getGeneralizedAmount(
                    priceInfo?.amount || 0
                  );
                  const itemTotal = Method.getGeneralizedAmount(
                    priceInfo?.totalAmount || 0
                  );

                  // Indent product rows slightly by leaving first column empty or just listing them
                  worksheetData.push([
                    "", // Date (empty to show hierarchy)
                    "", // ID (empty)
                    "", // Customer (empty)
                    itemName,
                    quantity,
                    `${price}`,
                    `${itemTotal}`,
                    "", // Remaining Amount (empty for products)
                  ]);
                });
              } else {
                worksheetData.push([
                  "",
                  "",
                  "",
                  "No products found",
                  "",
                  "",
                  "",
                  "",
                ]);
              }
              // Add a small separator row between orders
              worksheetData.push(["", "", "", "", "", "", "", ""]);
            });
          } else {
            // No orders found
            worksheetData.push(["No orders found for this route"]);
          }

          // Small delay to prevent overwhelming the server
          await new Promise((resolve) => setTimeout(resolve, 100));
        } catch (error) {
          console.error(
            `Error fetching details for route ${plan.route}:`,
            error
          );
          worksheetData.push(["Error fetching details for this route"]);
        }

        // Create worksheet for this route plan
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

        // Set column widths based on new structure
        worksheet["!cols"] = [
          { width: 15 }, // Order Date
          { width: 15 }, // Order ID
          { width: 25 }, // Customer Name
          { width: 30 }, // Item Name
          { width: 10 }, // Quantity
          { width: 15 }, // Price
          { width: 15 }, // Total Amount
          { width: 15 }, // Remaining Amount
        ];

        // Add styling for hierarchical structure
        const range = XLSX.utils.decode_range(worksheet["!ref"] || "A1");

        // Style header rows
        for (let col = range.s.c; col <= range.e.c; col++) {
          // Style first header row (plan headers)
          const cellRef1 = XLSX.utils.encode_cell({ r: 0, c: col });
          if (worksheet[cellRef1]) {
            worksheet[cellRef1].s = {
              font: { bold: true, color: { rgb: "FFFFFF" } },
              fill: { fgColor: { rgb: "4472C4" } }, // Plan header color
              alignment: { horizontal: "center" },
            };
          }
          // Style Order/Product headers (row index might vary, but here we added them at a fixed place relative to data pushes)
          // But since data length varies, we can't easily iterate by exact row number for dynamic headers unless we track it
          // A simpler approach for this task is to ensure the plan headers are styled.
          // You can stick to basic styling or enhance it if needed.
        }

        // Style plan summary row
        for (let col = 0; col <= 5; col++) {
          const cellRef = XLSX.utils.encode_cell({ r: 1, c: col });
          if (worksheet[cellRef]) {
            worksheet[cellRef].s = {
              font: { bold: true },
              fill: { fgColor: { rgb: "E7E6E6" } },
            };
          }
        }

        // Add sheet to workbook with unique name (driver + date)
        const driverName = plan.driver?.name || "Unknown";
        const planDate = plan.planDate
          ? Method.convertDateToFormat(plan.planDate, "DD-MM-YY")
          : processedCount + 1;
        const sheetName = `${driverName} ${planDate}`
          .replace(/[\\/*?:[\]]/g, "_")
          .substring(0, 31);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

        processedCount++;
        setProcessedRecords(processedCount);
        setProgress((processedCount / total) * 100);

        // Small delay to prevent overwhelming the server
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      // Create and download Excel file
      setCurrentStep("Generating Excel file...");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `driver-reconciliation-report-${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setCurrentStep("Export completed successfully!");
      setTimeout(() => {
        onHide();
      }, 2000);
    } catch (error) {
      console.error("Export error:", error);
      setError("Export failed: " + (error as Error).message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    if (isExporting) {
      if (
        window.confirm(
          "Export is in progress. Are you sure you want to cancel?"
        )
      ) {
        onHide();
      }
    } else {
      onHide();
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop={isExporting ? "static" : true}
      keyboard={!isExporting}
      centered
      size="lg"
    >
      <Modal.Header closeButton={!isExporting}>
        <Modal.Title>Export Route Product Report</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!isExporting && !error && (
          <div>
            <div className="alert alert-info">
              <h6>
                <strong>Export Information:</strong>
              </h6>
              <ul className="mb-2">
                <li>This will export all route product data to Excel format</li>
                <li>Each route plan will have a separate sheet</li>
                <li>Large datasets may take several minutes to process</li>
                <li>Please keep this window open during export</li>
                <li>The file will automatically download when ready</li>
              </ul>
            </div>

            <div className="alert alert-warning">
              <strong>Note:</strong> For datasets with many routes, the export
              may take a few minutes. Please be patient and do not close this
              window.
            </div>
          </div>
        )}

        {isExporting && (
          <div>
            <div className="d-flex align-items-center mb-3">
              <Spinner animation="border" size="sm" className="me-3" />
              <strong>Exporting Data...</strong>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between mb-2">
                <span>Progress:</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="progress">
                <div
                  className={`progress-bar ${
                    progress === 100 ? "bg-success" : "bg-primary"
                  }`}
                  role="progressbar"
                  style={{ width: `${progress}%` }}
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                ></div>
              </div>
            </div>

            {totalRecords > 0 && (
              <div className="mb-3">
                <small className="text-muted">
                  Processed: {processedRecords.toLocaleString()} of{" "}
                  {totalRecords.toLocaleString()} route plans
                </small>
              </div>
            )}

            <div className="mb-2">
              <small>{currentStep}</small>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger">
            <strong>Error:</strong> {error}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!isExporting && (
          <>
            <Button className="uw-btn uw-btn-light"
              data-analytics-event="finance_reports_driver_reconciliation_report_cancel_clicked" variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button className="uw-btn uw-btn-primary"
              data-analytics-event="finance_reports_driver_reconciliation_report_start_export_clicked" variant="primary" onClick={handleExport}>
              Start Export
            </Button>
          </>
        )}

        {isExporting && (
          <Button className="uw-btn uw-btn-light"
            data-analytics-event="finance_reports_driver_reconciliation_report_cancel_export_clicked" variant="outline-secondary" onClick={handleClose}>
            Cancel Export
          </Button>
        )}

        {error && (
          <Button className="uw-btn uw-btn-primary"
            data-analytics-event="finance_reports_driver_reconciliation_report_try_again_clicked"
            variant="primary"
            onClick={() => {
              setError("");
              setProgress(0);
              setCurrentStep("");
            }}
          >
            Try Again
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const DriverReconciliationReport = () => {
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const [endDate, setEndDate] = useState<any>(new Date());
  const [startDate, setStartDate] = useState<any>(new Date());
  const [fetchLoading, setFetchLoading] = useState(false);
  const [routePlans, setRoutePlans] = useState<any>([]);
  const [download, setDownload] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [drivers, setDrivers] = useState<any>([]);
  const [selectedDriver, setSelectedDriver] = useState<any>(null);

  // New state for expandable rows
  const [expandedRoute, setExpandedRoute] = useState<any>({});
  const [routeDetails, setRouteDetails] = useState<any>({});
  const [loadingRouteDetails, setLoadingRouteDetails] = useState<any>({});

  // New state for expandable order rows
  const [expandedOrder, setExpandedOrder] = useState<any>({});

  useEffect(() => {
    (async () => {
      setFetchLoading(true);
      const warehouseValue = selectedWarehouse?.value || null;
      await fetchRoutePlans(startDate, endDate, warehouseValue || null, null);
      setFetchLoading(false);
    })();
  }, [selectedWarehouse]);

  const fetchRoutePlans = async (
    startDate: string,
    endDate: string,
    warehouse?: any,
    driverId?: any
  ) => {
    let params: any = {
      fromDate: startDate
        ? Method.convertDateToFormat(startDate, "YYYY-MM-DD")
        : "",
      toDate: endDate ? Method.convertDateToFormat(endDate, "YYYY-MM-DD") : "",
      needCount: true,
    };
    if (warehouse) {
      params.warehouse = warehouse;
    }
    if (driverId) {
      params.driver = driverId;
    }
    let apiService = new APICallService(
      routePlanning.listOutForDeliveryPlans,
      params,
      "",
      "",
      "",
      "",
      AllModules,
      warehouse
    );
    let response = await apiService.callAPI();
    if (response && response.records) {
      setRoutePlans(response.records);

      // Extract unique drivers from the response
      const uniqueById = (() => {
        const map = new Map();
        for (const d of response.records) {
          if (d.driver) {
            if (!d || !d.driver.reference) continue; // skip invalid items
            if (!map.has(d.driver.reference)) map.set(d.driver.reference, d);
          }
        }
        return Array.from(map.values());
      })();

      const driverData = uniqueById.map((val) => ({
        label: val.driver.name,
        value: val.driver.name,
        title: val.driver.name,
        _id: val.driver.reference,
      }));
      setDrivers(driverData);

      // Reset expanded state when filter changes
      setExpandedRoute({});
      setRouteDetails({});
    } else {
      setRoutePlans([]);
      if (!driverId) {
        setDrivers([]);
      }
    }
  };

  const fetchRouteDetails = async (
    routeId: string,
    vehicleId: string,
    warehouse?: any
  ) => {
    setLoadingRouteDetails((prev: any) => ({ ...prev, [routeId]: true }));
    const params: any = { forLoading: false };
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    let apiService = new APICallService(
      routePlanning.vehicleInfo,
      params,
      { routeId: routeId, vehicleId: vehicleId },
      "",
      "",
      "",
      AllModules,
      warehouse
    );
    let response = await apiService.callAPI();
    if (response) {
      if (response.orders.length)
        setRouteDetails((prev: any) => ({
          ...prev,
          [routeId]: response.orders || [],
        }));
    }
    setLoadingRouteDetails((prev: any) => ({ ...prev, [routeId]: false }));
  };

  const handleChange = async ([startDate, endDate]: any) => {
    setStartDate(startDate);
    setEndDate(endDate);
    if (startDate && endDate) {
      setFetchLoading(true);
      await fetchRoutePlans(
        startDate,
        endDate,
        selectedWarehouse?.value || null,
        selectedDriver ? selectedDriver._id : null
      );
      setFetchLoading(false);
    }
  };

  const handleDownload = () => {
    setShowExportModal(true);
  };

  const handleDriverFilter = async (event: any) => {
    if (event) {
      setFetchLoading(true);
      setSelectedDriver(event);
      await fetchRoutePlans(
        startDate,
        endDate,
        selectedWarehouse?.value || null,
        event._id
      );
      setFetchLoading(false);
    } else {
      setFetchLoading(true);
      setSelectedDriver(null);
      await fetchRoutePlans(
        startDate,
        endDate,
        selectedWarehouse?.value || null,
        null
      );
      setFetchLoading(false);
    }
  };

  const toggleRouteDetails = (routeId: string, vehicleId: string) => {
    setExpandedRoute((prev: any) => ({
      ...prev,
      [routeId]: !prev[routeId],
    }));

    if (!routeDetails[routeId] && !expandedRoute[routeId]) {
      fetchRouteDetails(routeId, vehicleId, selectedWarehouse?.value || null);
    }
  };

  const toggleOrderDetails = (orderId: string) => {
    setExpandedOrder((prev: any) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  return (
    <>
      {showPermissionModal && (
        <PermissionModal
          show={showPermissionModal}
          onHide={() => setShowPermissionModal(false)}
          moduleName={"orders & delivery"}
        />
      )}
      <ExcelExportModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        startDate={startDate}
        endDate={endDate}
        driverId={selectedDriver ? selectedDriver._id : null}
      />
      <div className="p-5">
        <Row className="g-4">
          <Col md>
            <div className="d-flex align-items-center mt-4">
              <h1 className="fs-22 fw-bolder">Driver Reconciliation Report</h1>
            </div>
          </Col>
          <Col md="auto">
            <div className="d-flex align-items-center">
              <FormLabel className="fs-16 fw-500">Filter by Driver</FormLabel>
              <div className="ms-5">
                <CustomSelect
                  className="form-control bg-white min-h-30px fs-16 fw-bold text-dark min-w-md-288px min-w-175px"
                  onChange={handleDriverFilter}
                  options={drivers}
                  isClearable={true}
                  value={selectedDriver}
                  default={selectedDriver}
                />
              </div>
            </div>
          </Col>
          <Col md="auto">
            <div className="d-flex align-items-center">
              <FormLabel className="fs-16 fw-500">Filter by dates</FormLabel>
              <div className="ms-5">
                <CustomDatePicker
                  className="form-control bg-white min-h-30px fs-16 fw-bold text-dark min-w-md-288px min-w-175px"
                  onChange={handleChange}
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat="dd/MM/yyyy"
                  showFullMonthYearPicker={true}
                  maxDate={new Date()}
                  inputTextBG="bg-white"
                />
              </div>
            </div>
          </Col>
          <Col md="auto">
            <Button
              data-analytics-event="finance_reports_driver_reconciliation_report_download_clicked"
              className="uw-btn uw-btn-primary fs-14 fw-600"
              onClick={handleDownload}
              disabled={fetchLoading}
            >
              <span className="indicator-label fs-16 fw-bold">
                Download Excel
              </span>
            </Button>
          </Col>
        </Row>

        <Card className="border border-r10px mt-6">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <table className="table table-row-bordered datatable align-middle gs-7 gy-4 my-0">
                <thead>
                  <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                    <th className="min-w-md-175px min-w-150px">Plan Date</th>
                    <th className="min-w-md-200px min-w-125px">Driver</th>
                    <th className="min-w-md-200px min-w-125px">Vehicle</th>
                    <th className="min-w-md-200px min-w-125px">Total Orders</th>
                    <th className="min-w-md-200px min-w-125px">Total Amount</th>
                    <th className="min-w-md-200px min-w-125px">Remaining Amount</th>
                    <th className="min-w-150px text-end"></th>
                  </tr>
                </thead>
                <tbody className="fs-15 fw-600">
                  {fetchLoading ? (
                    <tr>
                      <td colSpan={5}>
                        <div className="d-flex justify-content-center">
                          <Loader loading={fetchLoading} />
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {routePlans.length ? (
                        routePlans.map((plan: any, index: number) => (
                          <div key={index} style={{ display: "contents" }}>
                            <tr
                              className=""
                              onClick={() =>
                                toggleRouteDetails(
                                  plan.route,
                                  plan.vehicle.reference
                                )
                              }
                              style={{ cursor: "pointer" }}
                            >
                              <td>
                                <span className="text-dark d-block">
                                  {Method.convertDateToDDMMYYYY(
                                    plan.deliveryStartedAt
                                  )}
                                </span>
                              </td>
                              <td>
                                <span className="text-dark d-block">
                                  {plan.driver?.name || "N/A"}
                                </span>
                              </td>
                              <td className="pe-5">
                                <span className="text-dark d-block">
                                  {plan.vehicle?.name +
                                    " - " +
                                    plan.vehicle?.number || "N/A"}
                                </span>
                              </td>
                              <td className="ps-5">
                                <span className="text-dark d-block">
                                  {plan.totalOrders > 1
                                    ? plan.totalOrders + " orders"
                                    : "1 order"}
                                </span>
                              </td>
                              <td>
                                <span className="text-dark d-block">
                                  TSh{" "}
                                  {plan.latestTotalCashCollection > 0 ? Method.getGeneralizedAmount(plan.latestTotalCashCollection) : Method.getGeneralizedAmount(
                                    plan.totalCashCollection
                                  )}
                                </span>
                              </td>
                              <td>
                                <span className={`${plan.latestPendingCashCollection > 0 ? "text-danger" : "text-success"} d-block`}>
                                  TSh{" "}
                                  {plan.latestPendingCashCollection > 0 ? Method.getGeneralizedAmount(plan.latestPendingCashCollection) : 0}
                                </span>
                              </td>
                              <td className="text-end">
                                <Button
                                  variant="link"
                                  className="p-0 me-2 text-muted"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRouteDetails(
                                      plan.route,
                                      plan.vehicle.reference
                                    );
                                  }}
                                  data-analytics-event="finance_reports_driver_reconciliation_report_toggle_route_details_clicked"
                                >
                                  <i
                                    className={`fas fa-chevron-${
                                      expandedRoute[plan.route] ? "up" : "down"
                                    }`}
                                  ></i>
                                </Button>
                              </td>
                            </tr>
                            {expandedRoute[plan.route] && (
                              <tr>
                                <td colSpan={7} className="p-0">
                                  <div className="p-3 ps-8">
                                    {loadingRouteDetails[plan.route] ? (
                                      <div className="d-flex justify-content-center">
                                        <Spinner animation="border" size="sm" />
                                      </div>
                                    ) : routeDetails[plan.route] &&
                                      routeDetails[plan.route].length > 0 ? (
                                      <div className="table-responsive rounded border p-3">
                                        <h5 className="mb-3 fw-bold">
                                          Order Details
                                        </h5>
                                        <table className="table table-row-bordered align-middle gs-7 gy-4">
                                          <thead>
                                            <tr className="fw-bold fs-14 text-muted border-bottom">
                                              <th className="min-w-100px">
                                                Order Date
                                              </th>
                                              <th className="min-w-100px">
                                                Order ID
                                              </th>
                                              <th className="min-w-150px">
                                                Customer Name
                                              </th>
                                              <th className="min-w-100px ">
                                                Total Amount
                                              </th>
                                              <th className="min-w-100px ">
                                                Remaining Amount
                                              </th>
                                              <th className="w-50px"></th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {routeDetails[plan.route].map(
                                              (
                                                order: any,
                                                orderIndex: number
                                              ) => (
                                                <div
                                                  key={order._id || orderIndex}
                                                  style={{
                                                    display: "contents",
                                                  }}
                                                >
                                                  <tr
                                                    className=""
                                                    onClick={() =>
                                                      toggleOrderDetails(
                                                        order._id
                                                      )
                                                    }
                                                    style={{
                                                      cursor: "pointer",
                                                    }}
                                                  >
                                                    <td>
                                                      {Method.convertDateToDDMMYYYY(
                                                        order._createdAt ||
                                                          order.scheduleDate
                                                      )}
                                                    </td>
                                                    <td>#{order.refKey}</td>
                                                    <td>
                                                      {order.customer?.name ||
                                                        order.placedBy?.user
                                                          ?.name ||
                                                        "N/A"}
                                                    </td>
                                                    <td className="fw-bold">
                                                      TSh{" "}
                                                      {Method.getGeneralizedAmount(
                                                        order.routeVehicle
                                                          ?.paymentCollection
                                                          ?.payment
                                                          ?.totalCharge || 0
                                                      )}
                                                    </td>
                                                    <td className="fw-bold">
                                                      <span className={`${!order.payment.completed && order.payment.remainingCharge > 0 ? "text-danger" : "text-success"}`}>
                                                        TSh{" "}
                                                        {!order.payment.completed ? Method.getGeneralizedAmount(
                                                          order
                                                            ?.payment
                                                            ?.remainingCharge || 0
                                                        ) : 0}
                                                      </span>
                                                    </td>
                                                    <td className="text-end">
                                                      <i
                                                        className={`fas fa-chevron-${
                                                          expandedOrder[
                                                            order._id
                                                          ]
                                                            ? "up"
                                                            : "down"
                                                        } text-muted`}
                                                      ></i>
                                                    </td>
                                                  </tr>
                                                  {expandedOrder[order._id] && (
                                                    <tr>
                                                      <td
                                                        colSpan={5}
                                                        className="p-0"
                                                      >
                                                        <div className="p-3 ps-8">
                                                          <div className="table-responsive bg-white rounded border shadow-sm">
                                                            <table className="table table-sm table-bordered mb-0">
                                                              <thead>
                                                                <tr className="bg-light fw-bold fs-13 text-gray-600">
                                                                  <th className="ps-4 min-w-150px">
                                                                    Product Name
                                                                  </th>
                                                                  <th className="min-w-100px ">
                                                                    Quantity
                                                                  </th>
                                                                  <th className="min-w-100px">
                                                                    Price
                                                                  </th>
                                                                  <th className="min-w-100px  pe-4">
                                                                    Total Amount
                                                                  </th>
                                                                </tr>
                                                              </thead>
                                                              <tbody>
                                                                {order
                                                                  .routeVehicle
                                                                  ?.variants
                                                                  ?.length >
                                                                0 ? (
                                                                  order.routeVehicle.variants.map(
                                                                    (
                                                                      item: any,
                                                                      idx: number
                                                                    ) => {
                                                                      // Find matching price info details in order.variants using variant ID
                                                                      const priceInfo =
                                                                        order.variants?.find(
                                                                          (
                                                                            v: any
                                                                          ) =>
                                                                            (v
                                                                              .variant
                                                                              ?._id ||
                                                                              v.variant) ===
                                                                            (item
                                                                              .variant
                                                                              ?._id ||
                                                                              item.variant)
                                                                        );

                                                                      return (
                                                                        <tr
                                                                          key={
                                                                            idx
                                                                          }
                                                                        >
                                                                          <td className="ps-4">
                                                                            {item
                                                                              .variant
                                                                              ?.title ||
                                                                              "N/A"}
                                                                          </td>
                                                                          <td className="">
                                                                            {item.stockCount ||
                                                                              0}
                                                                          </td>
                                                                          <td className="">
                                                                            TSh{" "}
                                                                            {Method.getGeneralizedAmount(
                                                                              priceInfo?.amount ||
                                                                                0
                                                                            )}
                                                                          </td>
                                                                          <td className=" pe-4 fw-bold">
                                                                            TSh{" "}
                                                                            {Method.getGeneralizedAmount(
                                                                              priceInfo?.totalAmount ||
                                                                                0
                                                                            )}
                                                                          </td>
                                                                        </tr>
                                                                      );
                                                                    }
                                                                  )
                                                                ) : (
                                                                  <tr>
                                                                    <td
                                                                      colSpan={
                                                                        4
                                                                      }
                                                                      className="text-center py-3 text-muted"
                                                                    >
                                                                      No
                                                                      products
                                                                      found
                                                                    </td>
                                                                  </tr>
                                                                )}
                                                              </tbody>
                                                            </table>
                                                          </div>
                                                        </div>
                                                      </td>
                                                    </tr>
                                                  )}
                                                </div>
                                              )
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    ) : (
                                      <div className="text-center text-muted fw-bold py-4">
                                        No orders found for this route.
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </div>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5}>
                            <div className="w-100 fs-15 fw-bold d-flex justify-content-center">
                              No Route Plans found
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};
export default DriverReconciliationReport;
